import { startOfDay } from "date-fns"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { isCustomerStop } from "@/lib/haulageUtils"
import type { PlannedRoute } from "@/types/collections"

/** Haulage sellable start: route end minus driver_max_hours_per_day, in absolute time so overnight routes work. */
export function HaulageSellableMarker({
  route,
  driverMaxHours,
  selectedDate,
  getTimePosition,
}: {
  route: PlannedRoute
  driverMaxHours: number
  selectedDate: Date
  getTimePosition: (time: Date) => number
}) {
  if (driverMaxHours <= 0 || !route.stops?.length) return null
  // Last stop's departure (any type), so the window includes the return to depot.
  const lastStop = route.stops[route.stops.length - 1]
  const routeEnd = new Date(lastStop.plannedDeparture)
  const earliestStart = new Date(
    routeEnd.getTime() - driverMaxHours * 60 * 60 * 1000,
  )
  const customerStops = route.stops.filter(isCustomerStop)
  if (customerStops.length === 0) return null
  const firstArrival = new Date(customerStops[0].plannedArrival)
  if (earliestStart.getTime() >= firstArrival.getTime()) return null
  const selectedDayStart = startOfDay(selectedDate)
  const sellableSpansPreviousDay =
    earliestStart.getTime() < selectedDayStart.getTime()
  const haulageMarkerWidth = 28
  const startLeft = getTimePosition(earliestStart) // clamps to 0 when earliestStart is before selected day
  const firstStopLeft = getTimePosition(firstArrival)
  const hasBefore = firstStopLeft - startLeft >= haulageMarkerWidth + 4
  if (!hasBefore) return null
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="absolute top-1.5 bottom-1.5 rounded border-2 border-dashed border-emerald-500 bg-emerald-50 flex items-center justify-center z-[5]"
            style={{
              left: `${startLeft}px`,
              width: `${haulageMarkerWidth}px`,
              minWidth: `${haulageMarkerWidth}px`,
            }}
          >
            <span className="text-[9px] font-medium text-emerald-700">
              +Stops
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs font-medium text-emerald-800">
            Haulage: extended empty leg
          </p>
          <p className="text-[10px] text-palette-jungleGreen600">
            Total route window is {driverMaxHours}h (overnight routes use full
            12h from route end).
            {sellableSpansPreviousDay &&
              " Sellable leg starts on previous day; bar shows time on this day."}{" "}
            Driver could start earlier; add stops along this leg for more
            revenue.
          </p>
        </TooltipContent>
      </Tooltip>
      <div
        className="absolute top-1/2 h-1 -translate-y-1/2 z-[5] rounded-full bg-emerald-400 border-0"
        style={{
          left: `${startLeft + haulageMarkerWidth}px`,
          width: `${firstStopLeft - (startLeft + haulageMarkerWidth)}px`,
        }}
      />
    </>
  )
}
